import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import Login from './components/Login';
import ErrorBoundary from './components/ErrorBoundary';
import reportWebVitals from './reportWebVitals';
import { supabase } from './lib/supabase';

// Global error handlers
window.addEventListener('error', (event) => {
  console.error('Global error caught:', event.error);
  event.preventDefault();
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  event.preventDefault();
});

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes

function AuthGate() {
  const [user, setUser] = useState(undefined); // undefined = loading

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        // "Remember me" = OFF: sign out if this is a new browser session
        const noPersist = localStorage.getItem('vf_no_persist');
        const sessionActive = sessionStorage.getItem('vf_session_active');
        if (noPersist && !sessionActive) {
          supabase.auth.signOut();
          localStorage.removeItem('vf_no_persist');
          setUser(null);
          return;
        }
      }
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Inactivity auto-logout
  useEffect(() => {
    if (!user) return;
    let timer = setTimeout(() => supabase.auth.signOut(), INACTIVITY_MS);
    const reset = () => { clearTimeout(timer); timer = setTimeout(() => supabase.auth.signOut(), INACTIVITY_MS); };
    window.addEventListener('mousemove', reset);
    window.addEventListener('keydown', reset);
    window.addEventListener('click', reset);
    window.addEventListener('scroll', reset);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousemove', reset);
      window.removeEventListener('keydown', reset);
      window.removeEventListener('click', reset);
      window.removeEventListener('scroll', reset);
    };
  }, [user]);

  if (user === undefined) return null;
  if (!user) return <Login onLogin={setUser} />;
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <AuthGate />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
